import { useEffect, useRef, useState } from "react";
import { useScroll, useTransform } from "framer-motion";
import { Camera, GraduationCap, Sparkles } from "lucide-react";
import { MaskLine, Reveal, SectionLabel } from "./motion";
import { Countdown } from "./Countdown";
import { SubscribeForm } from "./SubscribeForm";
import { WaitlistCounter } from "./WaitlistCounter";
import { FlashPackBonus } from "./FlashPackBonus";
import { PhoneShowcase } from "./inkmaster/PhoneShowcase";
import { api } from "@/lib/api";

const FEATURES = [
  { icon: GraduationCap, t: "Impara l'arte del tatuaggio", d: "Livelli guidati che ti portano dalle linee base agli stili complessi: Old School, Giapponese, Realistico." },
  { icon: Camera, t: "Prova sulla pelle con la fotocamera AR", d: "Scegli qualsiasi disegno e provalo in realtà aumentata direttamente sul tuo corpo, prima di renderlo per sempre." },
  { icon: Sparkles, t: "Generatore IA di flash", d: "Descrivi il tattoo che hai in mente, scegli lo stile e l'IA crea nuovi flash unici, solo tuoi." },
];

export const AppSection = () => {
  const ref = useRef(null);
  const [count, setCount] = useState(null);
  useEffect(() => {
    api.get("/waitlist/count").then(({ data }) => setCount(data.count)).catch(() => {});
  }, []);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [-32, -10, 14]);
  const rotateX = useTransform(scrollYProgress, [0, 1], [16, -8]);
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section id="app" ref={ref} data-testid="app-section" className="relative overflow-hidden border-t border-line bg-ink-2 px-5 py-24 sm:px-8 lg:px-12 lg:py-36">
      <div aria-hidden className="pointer-events-none absolute right-[10%] top-1/4 h-[480px] w-[480px] rounded-full bg-acid/10 blur-[150px]" />
      <div className="relative grid items-center gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionLabel index="05">La mia app</SectionLabel>
          <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
            <MaskLine className="text-acid">InkMaster.</MaskLine>
            <MaskLine delay={0.1}>Impara a tatuare,</MaskLine>
            <MaskLine delay={0.2} className="text-outline">sta arrivando.</MaskLine>
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-bone/70 sm:text-lg" data-testid="app-description">
              InkMaster è l'app per <strong className="font-semibold text-acid">imparare a tatuare</strong>, pensata per chi parte da zero e per chi vuole crescere davvero: ti alleni con la tecnica vera livello dopo livello, provi i tuoi disegni sulla pelle con la fotocamera e crei nuovi flash con l'IA. Dalla prima linea al primo pezzo vero, tutto in un'unica app. Presto su App Store e Google Play.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-px border border-line bg-line sm:grid-cols-3" data-testid="app-features">
            {FEATURES.map(({ icon: Icon, t, d }, i) => (
              <Reveal key={t} delay={0.1 + i * 0.08} className="bg-ink-2 p-5" data-testid={`app-feature-${i + 1}`}>
                <Icon className="h-5 w-5 text-acid" />
                <p className="mt-4 font-display text-sm font-bold uppercase">{t}</p>
                <p className="mt-2 text-sm leading-relaxed text-bone/60">{d}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10 max-w-xl">
            <Countdown />
            <div className="mt-10">
              <FlashPackBonus />
            </div>
            <div className="mb-2 mt-8 flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">Avvisami quando esce l&apos;app</p>
              <WaitlistCounter count={count} />
            </div>
            <SubscribeForm source="app" testidPrefix="app-notify" buttonLabel="Avvisami" successText="Sei in lista! Sarai tra i primi a scaricare InkMaster." onSubscribed={(d) => setCount(d.count)} />
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <PhoneShowcase tilt={{ rotateY, rotateX, y }} />
        </div>
      </div>
    </section>
  );
};
